import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { ButtonLink } from '@/components/ui/Button'
import { Page, PageHeader } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { daysUntil, formatAgo, formatDateTerse } from '@/lib/dates'
import { formatCouple, pluralise, townFrom } from '@/lib/format'
import { describeError } from '@/lib/supabase'
import { STATUS_LABEL, STATUS_TONE } from '@/features/inquiries/EnquiriesPage'
import { inquiryUrgency, useInquiries, type InquiryWithRelations } from '@/features/inquiries/queries'
import { JobCard } from './JobCard'
import { useBoard, useStages, type BoardJob } from './queries'

/** The first column holds enquiries as well as weddings not yet booked. */
const ENQUIRIES = 'enquiries'

type Card = { type: 'job'; job: BoardJob } | { type: 'enquiry'; inquiry: InquiryWithRelations }

/**
 * Projects: the board of every wedding, in five columns that fill themselves
 * (27 Oct 2026). Nothing is dragged any more: the database decides the column
 * from what she ticks and the date (job_stage_name in the migration):
 *
 *   Enquiries   open enquiries, and weddings until the contract is signed and
 *               the deposit is in
 *   Booked      contract signed and deposit received
 *   Shoot week  the 7 days before the wedding
 *   Editing     from the day after
 *   Delivered   the photos and the film delivered
 *   Albums      delivered, with an album still to deliver (last column)
 *
 * Columns are separated by a single vertical hairline and cards by horizontal
 * ones, so the board is drawn entirely in rules. On a phone it becomes one
 * column at a time behind a stage picker.
 */
export function PipelinePage() {
  const stagesQuery = useStages()
  const boardQuery = useBoard()
  const enquiriesQuery = useInquiries('open')
  const [mobileStageId, setMobileStageId] = useState<string | null>(null)

  const stages = stagesQuery.data ?? []
  const jobs = boardQuery.data ?? []

  const byStage = useMemo(() => {
    const grouped = new Map<string, Card[]>()
    const first = (stagesQuery.data ?? []).find((stage) => stage.name.toLowerCase() === ENQUIRIES)
    for (const stage of stagesQuery.data ?? []) grouped.set(stage.id, [])
    for (const job of boardQuery.data ?? []) {
      // A wedding made from an enquiry that was then lost has no place here.
      if (job.inquiry && (job.inquiry.status === 'lost' || job.inquiry.status === 'spam')) continue
      grouped.get(job.pipeline_stage_id)?.push({ type: 'job', job })
    }
    if (first) {
      const bucket = grouped.get(first.id) ?? []
      // Enquiries not yet turned into a wedding (once they are, the wedding card shows).
      for (const inquiry of enquiriesQuery.data ?? []) {
        if (!inquiry.job) bucket.unshift({ type: 'enquiry', inquiry })
      }
      grouped.set(first.id, bucket)
    }
    return grouped
  }, [stagesQuery.data, boardQuery.data, enquiriesQuery.data])

  const activeMobileStage = mobileStageId ?? stages[0]?.id ?? null

  if (stagesQuery.isPending || boardQuery.isPending) return <Loading label="Loading your projects" />

  if (stagesQuery.isError || boardQuery.isError) {
    return (
      <Page>
        <ErrorState
          title="Projects could not load"
          detail={describeError(stagesQuery.error ?? boardQuery.error)}
          onRetry={() => {
            void stagesQuery.refetch()
            void boardQuery.refetch()
          }}
        />
      </Page>
    )
  }

  const upcoming = jobs.filter((job) => job.booked_at && daysUntil(job.event_date) >= 0).length
  const waiting = (enquiriesQuery.data ?? []).filter((inquiry) => inquiry.status === 'new').length

  return (
    <Page className="max-w-none">
      <PageHeader
        eyebrow="Operations"
        title="Projects"
        meta={
          <>
            <span>{upcoming} booked still to come</span>
            {waiting > 0 ? <span className="text-amber">{pluralise(waiting, 'enquiry', 'enquiries')} awaiting a reply</span> : null}
          </>
        }
        actions={
          <>
            <ButtonLink to="/enquiries/new">Log an enquiry</ButtonLink>
            <ButtonLink to="/weddings/new" variant="primary">
              New project
            </ButtonLink>
          </>
        }
      />

      {jobs.length === 0 && (enquiriesQuery.data ?? []).length === 0 ? (
        <EmptyState
          className="mt-4"
          title="Nothing on the board yet"
          body="Enquiries from the website arrive in the first column. You can also add a wedding that was booked before this system existed."
        >
          <div className="mt-2 flex gap-2">
            <ButtonLink to="/weddings/new" size="sm" variant="primary">
              Add a wedding
            </ButtonLink>
          </div>
        </EmptyState>
      ) : (
        <>
          {/* Mobile: one stage at a time */}
          <div className="lg:hidden">
            <div className="-mx-5 flex gap-0 overflow-x-auto border-b px-5">
              {stages.map((stage) => {
                const count = byStage.get(stage.id)?.length ?? 0
                const active = stage.id === activeMobileStage
                return (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() => setMobileStageId(stage.id)}
                    className={cn(
                      'label shrink-0 border-b-2 px-3 py-2.5 whitespace-nowrap',
                      active ? 'border-b-accent font-semibold text-accent' : 'border-b-transparent',
                    )}
                  >
                    {stage.name} <span className="ml-1 opacity-60">{count}</span>
                  </button>
                )
              })}
            </div>

            <div className="mt-3 flex flex-col gap-2 rounded-xl bg-paper p-2">
              {(byStage.get(activeMobileStage ?? '') ?? []).map((card) => (
                <CardView key={cardKey(card)} card={card} />
              ))}
              {(byStage.get(activeMobileStage ?? '') ?? []).length === 0 ? (
                <p className="px-3 py-8 text-center text-[length:var(--text-sm)] text-taupe">Nothing in this stage.</p>
              ) : null}
            </div>
          </div>

          {/* Desktop: the board, Trello style: grey lists of floating cards. */}
          <div className="hidden lg:block">
            <div className="-mx-8 overflow-x-auto px-8 pb-2">
              <div className="flex w-max items-start gap-3">
                {stages.map((stage) => (
                  <section
                    key={stage.id}
                    className="flex w-[16rem] shrink-0 flex-col rounded-xl bg-paper p-2"
                    aria-label={stage.name}
                  >
                    <header className="flex items-baseline justify-between gap-2 px-2 pt-1 pb-2.5">
                      <h2 className="text-[length:var(--text-sm)] font-semibold">{stage.name}</h2>
                      <span className="text-[length:var(--text-xs)] text-taupe" data-numeric>
                        {byStage.get(stage.id)?.length ?? 0}
                      </span>
                    </header>
                    <div className="flex flex-col gap-2">
                      {(byStage.get(stage.id) ?? []).map((card) => (
                        <CardView key={cardKey(card)} card={card} />
                      ))}
                      {(byStage.get(stage.id) ?? []).length === 0 ? (
                        <p className="px-2 pb-2 text-[length:var(--text-xs)] text-taupe">Nothing here</p>
                      ) : null}
                    </div>
                    {stage.name.toLowerCase() === ENQUIRIES ? (
                      <Link
                        to="/enquiries"
                        className="mt-2 rounded-md px-2 py-1.5 text-[length:var(--text-xs)] text-taupe hover:bg-[var(--color-wash-strong)] hover:text-ink"
                      >
                        All enquiries, including lost ones
                      </Link>
                    ) : null}
                  </section>
                ))}
              </div>
            </div>

            <p className="mt-3 text-[length:var(--text-2xs)] text-taupe">
              Weddings move on their own: to Booked when the contract is signed and the deposit is in, to Shoot week 7
              days before, to Editing the day after, and to Delivered when the photos and the film are delivered (to Albums first if an
              album is still to come).
            </p>
          </div>
        </>
      )}
    </Page>
  )
}

function cardKey(card: Card) {
  return card.type === 'job' ? card.job.id : `enquiry-${card.inquiry.id}`
}

function CardView({ card }: { card: Card }) {
  if (card.type === 'enquiry') return <EnquiryCard inquiry={card.inquiry} />
  return <JobCard job={card.job} />

}

/** An enquiry on the board: who, when, where, and how long it has waited. */
function EnquiryCard({ inquiry }: { inquiry: InquiryWithRelations }) {
  const urgency = inquiryUrgency(inquiry)
  const town = inquiry.venue?.town ?? (inquiry.venue_text ? townFrom(inquiry.venue_text) : null)

  return (
    <Link
      to={`/enquiries/${inquiry.id}`}
      className="card block px-3 py-2.5 transition-shadow duration-[var(--dur-fast)] hover:ring-2 hover:ring-accent"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="display min-w-0 flex-1 truncate text-[0.9375rem] leading-tight">
          {formatCouple(
            { first_name: inquiry.first_name, last_name: inquiry.last_name },
            inquiry.partner_first_name ? { first_name: inquiry.partner_first_name, last_name: inquiry.partner_last_name } : null,
          )}
        </p>
        <Badge tone={STATUS_TONE[inquiry.status]}>{STATUS_LABEL[inquiry.status]}</Badge>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2 text-[length:var(--text-2xs)]">
        {inquiry.event_date ? (
          <time dateTime={inquiry.event_date} className="font-medium tracking-wide">
            {formatDateTerse(inquiry.event_date)}
          </time>
        ) : (
          <span className="text-taupe">No date yet</span>
        )}
        {town ? <span className="truncate text-taupe">{town}</span> : null}
      </div>
      <p
        className={cn(
          'mt-1 text-[length:var(--text-2xs)]',
          urgency === 'late' ? 'text-clay' : urgency === 'waiting' ? 'text-amber' : 'text-taupe',
        )}
      >
        Enquired {formatAgo(inquiry.received_at)}
      </p>
    </Link>
  )
}

/** Compact list used on the dashboard and the contact page. */
export function JobList({ jobs, emptyLabel = 'No weddings' }: { jobs: BoardJob[]; emptyLabel?: string }) {
  if (jobs.length === 0) {
    return <p className="py-6 text-center text-[length:var(--text-sm)] text-taupe">{emptyLabel}</p>
  }

  return (
    <ul className="border-t">
      {jobs.map((job) => (
        <li key={job.id} className="border-b">
          <Link
            to={`/weddings/${job.id}`}
            className="flex items-baseline justify-between gap-4 px-1 py-2 hover:bg-[var(--color-wash)]"
          >
            <span className="display truncate text-[0.9375rem]">
              {formatCouple(job.primary_contact, job.partner_contact)}
            </span>
            <span className="shrink-0 text-[length:var(--text-2xs)] text-taupe" data-numeric>
              {job.event_date}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
